import { useEffect, useRef, useState, type ComponentPropsWithoutRef, type ReactNode } from "react"

import { InstallCommand } from "@/components/InstallCommand"
import { CLOUDFLARE_STREAM_BASE_URL } from "@/lib/cloudflare-stream"
import { FastestGitNetworkIllustration } from "@/routes/-components/home-new/illustrations/CodeHostingTrailsIllustrations"

import { AntithesisSimulation } from "./antithesis-simulation"
import { ContinuityDiagram } from "./continuity-diagram"
import { EntireDbCellDiagram } from "./entiredb-cell-diagram"
import { FederatedSearch } from "./federated-search"
import { headingId } from "./heading-id"
import { InlineCopyCode } from "./inline-copy-code"
import { MediaTabs } from "./media-tabs"
import { MirrorPropagationDiagram } from "./mirror-propagation-diagram"
import { PhotoGrid } from "./photo-grid"
import { PushToSearchable } from "./push-to-searchable"
import { SearchmarkResults } from "./searchmark-results"
import { Pre } from "./shell-code-block"
import { SpokesDiagram } from "./spokes-diagram"
import { Translation } from "./translation"

function getTextContent(children: ReactNode): string {
  if (typeof children === "string") return children
  if (Array.isArray(children)) return children.map(getTextContent).join("")
  if (children && typeof children === "object" && "props" in children) {
    const el = children as { props: { children?: ReactNode } }
    return getTextContent(el.props.children)
  }
  return ""
}

function generateId(children: ReactNode): string {
  return headingId(getTextContent(children))
}

function VideoBlock({ src }: { src: string }) {
  const [error, setError] = useState(false)
  if (error) {
    return (
      <p className="my-6 rounded-lg border border-(--border-default) bg-raised p-4 text-center text-muted">
        Video failed to load.
      </p>
    )
  }
  return (
    <video
      src={src}
      autoPlay
      loop
      muted
      playsInline
      className="my-6 w-full rounded-lg border border-(--border-default)"
      onError={() => setError(true)}
    />
  )
}

const CLOUDFLARE_STREAM_SDK_ID = "cloudflare-stream-sdk"
const CLOUDFLARE_STREAM_SDK_URL = "https://embed.cloudflarestream.com/embed/sdk.latest.js"
const CLOUDFLARE_STREAM_PLAY_ROOT_MARGIN = "320px 0px"

let cloudflareStreamSdkPromise: Promise<void> | null = null

interface CloudflareStreamPlayer {
  addEventListener: (eventName: string, listener: () => void) => void
  autoplay: boolean
  loop: boolean
  muted: boolean
  play: () => Promise<void>
}

interface CloudflareStreamWindow extends Window {
  Stream?: (iframe: HTMLIFrameElement) => CloudflareStreamPlayer
}

interface CloudflareStreamVideoProps {
  aspectRatioPercent?: string
  bordered?: boolean
  className?: string
  loop?: boolean
  posterUrl?: string
  title: string
  videoId: string
}

function getCloudflareStreamThumbnailUrl(videoId: string) {
  return `${CLOUDFLARE_STREAM_BASE_URL}/${videoId}/thumbnails/thumbnail.jpg?time=&height=600`
}

function getCloudflareStreamIframeUrl(videoId: string, posterUrl: string, loop: boolean) {
  const params = new URLSearchParams({
    muted: "true",
    poster: posterUrl,
    preload: "auto",
  })
  if (loop) params.set("loop", "true")

  return `${CLOUDFLARE_STREAM_BASE_URL}/${videoId}/iframe?${params.toString()}`
}

function getCloudflareStreamAutoplayUrl(videoId: string, posterUrl: string, loop: boolean) {
  const params = new URLSearchParams({
    autoplay: "true",
    muted: "true",
    poster: posterUrl,
  })
  if (loop) params.set("loop", "true")

  return `${CLOUDFLARE_STREAM_BASE_URL}/${videoId}/iframe?${params.toString()}`
}

function loadCloudflareStreamSdk() {
  const streamWindow = window as CloudflareStreamWindow
  if (streamWindow.Stream) return Promise.resolve()
  if (cloudflareStreamSdkPromise) return cloudflareStreamSdkPromise

  cloudflareStreamSdkPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(CLOUDFLARE_STREAM_SDK_ID)
    if (existingScript) {
      if (existingScript.dataset["loaded"] === "true") {
        resolve()
        return
      }
      existingScript.addEventListener("load", () => resolve(), { once: true })
      existingScript.addEventListener("error", () => reject(), { once: true })
      return
    }

    const script = document.createElement("script")
    script.id = CLOUDFLARE_STREAM_SDK_ID
    script.src = CLOUDFLARE_STREAM_SDK_URL
    script.async = true
    script.addEventListener(
      "load",
      () => {
        script.dataset["loaded"] = "true"
        resolve()
      },
      { once: true },
    )
    script.addEventListener("error", () => reject(), { once: true })
    document.head.appendChild(script)
  })

  return cloudflareStreamSdkPromise
}

function CloudflareStreamVideo({
  aspectRatioPercent = "56.25%",
  bordered = true,
  className,
  loop = true,
  posterUrl,
  title,
  videoId,
}: CloudflareStreamVideoProps) {
  const frameRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const hasRequestedPlayRef = useRef(false)
  const thumbnailUrl = posterUrl ?? getCloudflareStreamThumbnailUrl(videoId)

  useEffect(() => {
    const frame = frameRef.current
    if (!frame) return

    hasRequestedPlayRef.current = false
    void loadCloudflareStreamSdk().catch(() => undefined)

    let playbackFallbackTimeout: ReturnType<typeof setTimeout> | undefined
    const autoplayUrl = getCloudflareStreamAutoplayUrl(videoId, thumbnailUrl, loop)

    const fallbackToAutoplay = () => {
      const iframe = iframeRef.current
      if (!iframe || iframe.src === autoplayUrl) return

      iframe.src = autoplayUrl
    }

    const playWhenReady = () => {
      if (hasRequestedPlayRef.current) return
      hasRequestedPlayRef.current = true

      const iframe = iframeRef.current
      if (!iframe) {
        hasRequestedPlayRef.current = false
        return
      }

      loadCloudflareStreamSdk()
        .then(() => {
          const stream = (window as CloudflareStreamWindow).Stream
          if (!stream) {
            fallbackToAutoplay()
            return
          }

          const player = stream(iframe)
          if (!player) {
            fallbackToAutoplay()
            return
          }

          let didStartPlayback = false
          const markPlaybackStarted = () => {
            didStartPlayback = true
            if (playbackFallbackTimeout) clearTimeout(playbackFallbackTimeout)
          }

          player.addEventListener("play", markPlaybackStarted)
          playbackFallbackTimeout = setTimeout(() => {
            if (!didStartPlayback) fallbackToAutoplay()
          }, 1500)
          player.autoplay = true
          player.loop = loop
          player.muted = true
          void player.play().then(markPlaybackStarted).catch(fallbackToAutoplay)
        })
        .catch(fallbackToAutoplay)
    }

    if (typeof IntersectionObserver === "undefined") {
      playWhenReady()
      return () => {
        hasRequestedPlayRef.current = false
        if (playbackFallbackTimeout) clearTimeout(playbackFallbackTimeout)
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting)

        if (isVisible) {
          playWhenReady()
          observer.disconnect()
        }
      },
      { rootMargin: CLOUDFLARE_STREAM_PLAY_ROOT_MARGIN, threshold: 0 },
    )

    observer.observe(frame)

    return () => {
      hasRequestedPlayRef.current = false
      if (playbackFallbackTimeout) clearTimeout(playbackFallbackTimeout)
      observer.disconnect()
    }
  }, [loop, thumbnailUrl, videoId])

  return (
    <div
      ref={frameRef}
      className={[
        "my-6 overflow-hidden rounded-lg bg-raised",
        bordered ? "border border-(--border-default)" : null,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ position: "relative", paddingTop: aspectRatioPercent }}
    >
      <iframe
        ref={iframeRef}
        src={getCloudflareStreamIframeUrl(videoId, thumbnailUrl, loop)}
        title={title}
        loading="eager"
        className="absolute top-0 left-0 size-full border-0"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
      />
    </div>
  )
}

/** The homepage's push-speed bar chart, as a figure inside an article. */
function SpeedComparison() {
  return (
    <figure className="not-prose my-10">
      <FastestGitNetworkIllustration />
    </figure>
  )
}

export const mdxComponents = {
  FederatedSearch,
  InlineCopyCode,
  MediaTabs,
  PushToSearchable,
  SearchmarkResults,
  SpokesDiagram,
  ContinuityDiagram,
  EntireDbCellDiagram,
  MirrorPropagationDiagram,
  AntithesisSimulation,
  SpeedComparison,
  Translation,
  h1: ({ children, ...props }: ComponentPropsWithoutRef<"h1">) => (
    <h1
      id={generateId(children)}
      className="mb-4 scroll-mt-24 font-headline text-4xl leading-tight text-balance text-default sm:text-5xl"
      {...props}
    >
      {children}
    </h1>
  ),
  h2: ({ children, ...props }: ComponentPropsWithoutRef<"h2">) => (
    <h2
      id={generateId(children)}
      className="mt-10 mb-4 scroll-mt-24 font-headline text-[1.65rem] text-balance text-default sm:text-[1.8rem]"
      {...props}
    >
      {children}
    </h2>
  ),
  h3: ({ children, ...props }: ComponentPropsWithoutRef<"h3">) => (
    <h3
      id={generateId(children)}
      className="mt-8 mb-3 scroll-mt-24 font-headline text-lg text-pretty text-default sm:text-xl"
      {...props}
    >
      {children}
    </h3>
  ),
  h4: ({ children, ...props }: ComponentPropsWithoutRef<"h4">) => (
    <h4
      id={generateId(children)}
      className="mt-6 mb-2 scroll-mt-24 font-sans text-base font-normal text-pretty text-default underline"
      {...props}
    >
      {children}
    </h4>
  ),
  p: ({ children }: ComponentPropsWithoutRef<"p">) => (
    <p className="my-4 text-base leading-relaxed text-muted">{children}</p>
  ),
  ul: ({ children }: ComponentPropsWithoutRef<"ul">) => (
    <ul className="my-4 list-disc space-y-2 pl-6 text-base">{children}</ul>
  ),
  ol: ({ children }: ComponentPropsWithoutRef<"ol">) => (
    <ol className="my-4 list-decimal space-y-2 pl-6 text-base">{children}</ol>
  ),
  li: ({ children }: ComponentPropsWithoutRef<"li">) => (
    <li className="leading-relaxed text-muted [&>ol]:mt-2 [&>ol]:mb-0 [&>ul]:mt-2 [&>ul]:mb-0 [&>ul]:list-[circle]">
      {children}
    </li>
  ),
  blockquote: ({ children }: ComponentPropsWithoutRef<"blockquote">) => (
    <blockquote className="my-6 border-l-2 border-default pl-4 text-muted italic">
      {children}
    </blockquote>
  ),
  strong: ({ children }: ComponentPropsWithoutRef<"strong">) => (
    <strong className="font-semibold">{children}</strong>
  ),
  hr: () => <hr className="my-10 border-t border-default" />,
  a: ({ href, children }: ComponentPropsWithoutRef<"a">) => (
    <a
      href={href}
      className="text-default underline decoration-hairline underline-offset-[0.22em] hover:text-muted"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  code: ({ children }: ComponentPropsWithoutRef<"code">) => {
    const code = getTextContent(children)

    if (code.startsWith("entire ")) {
      return <InlineCopyCode code={code} />
    }

    return (
      <code className="rounded-lg border border-default bg-raised px-1.5 py-0.5 text-xs text-default before:content-none after:content-none">
        {children}
      </code>
    )
  },
  pre: Pre,
  img: ({ src, alt, className, ...props }: ComponentPropsWithoutRef<"img">) =>
    src?.endsWith(".mp4") ? (
      <VideoBlock src={src} />
    ) : (
      <img
        {...props}
        src={src}
        alt={alt || ""}
        loading="lazy"
        decoding="async"
        className={["my-6 h-auto w-full rounded-lg border border-(--border-default)", className]
          .filter(Boolean)
          .join(" ")}
      />
    ),
  iframe: ({
    className,
    allow,
    referrerPolicy,
    allowFullScreen,
    ...props
  }: ComponentPropsWithoutRef<"iframe">) => (
    <iframe
      {...props}
      className={className ?? "my-6 aspect-video w-full rounded border-0"}
      allow={
        allow ??
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      }
      referrerPolicy={referrerPolicy ?? "strict-origin-when-cross-origin"}
      allowFullScreen={allowFullScreen ?? true}
    />
  ),
  table: ({ children }: ComponentPropsWithoutRef<"table">) => (
    <div className="my-6 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }: ComponentPropsWithoutRef<"thead">) => (
    <thead className="border-b border-default">{children}</thead>
  ),
  th: ({ children, style }: ComponentPropsWithoutRef<"th">) => (
    <th className="px-3 py-2 text-left font-semibold text-default" style={style}>
      {children}
    </th>
  ),
  td: ({ children, style }: ComponentPropsWithoutRef<"td">) => (
    <td className="border-t border-default px-3 py-2 text-muted" style={style}>
      {children}
    </td>
  ),
  InstallCommand,
  CloudflareStreamVideo,
  PhotoGrid,
} as const
